import React, { useEffect, useState, useCallback } from "react";
import * as actions from "./actions";
import {
  Box,
  Grid,
  TextField,
  Table,
  TableBody,
  Tooltip,
  TableRow,
  TableHead,
  TableCell,
  TableContainer,
  Paper,
  Button,
  Pagination,
  Typography,
  Fade,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SEO from "../../components/SEO";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { releaseFiltersForVideo } from "../../constants";
import moment from "moment";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import BookmarksIcon from "@mui/icons-material/Bookmarks";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { toastError } from "../../utils";
import SelectCmp from "react-select";
import SearchIcon from "@mui/icons-material/Search";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

const PageContainer = styled(Box)({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #e8eef3 0%, #dce4ec 100%)',
  padding: '32px 24px',
  position: 'relative',
  paddingBottom: '64px',
});

const PageHeader = styled(Box)({
  marginBottom: '32px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  flexWrap: 'wrap',
  gap: '16px',
  '& h1': {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    marginBottom: '8px',
    fontFamily: "'DM Sans', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
});

const SearchField = styled(TextField)({
  fontFamily: "'DM Sans', sans-serif",
  width: "100%",
  "& .MuiOutlinedInput-root": {
    backgroundColor: 'white',
    borderRadius: '12px',
    fontFamily: "'DM Sans', sans-serif",
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.12)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(102, 126, 234, 0.5)',
    },
    "&.Mui-focused fieldset": {
      borderColor: "#667eea",
      borderWidth: "2px",
    },
  },
  '& .MuiOutlinedInput-input': {
    color: '#202124',
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'DM Sans', sans-serif",
    color: '#5f6368',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#667eea',
  },
});

const AudioButton = styled(Button)({
  fontFamily: "'DM Sans', sans-serif",
  background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
  color: 'white',
  borderRadius: '12px',
  padding: '12px 24px',
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 12px rgba(26, 115, 232, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, #1557b0 0%, #00a589 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(26, 115, 232, 0.4)',
  },
});

const StyledTableContainer = styled(TableContainer)({
  borderRadius: '16px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  background: 'white',
  overflow: 'hidden',
});

const StyledTableHead = styled(TableHead)({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  '& th': {
    fontFamily: "'DM Sans', sans-serif",
    fontWeight: 700,
    color: 'white',
    fontSize: '0.9375rem',
    padding: '16px',
    borderBottom: 'none',
  },
});

const StyledTableCell = styled(TableCell)({
  fontFamily: "'DM Sans', sans-serif",
  fontSize: '0.875rem',
  padding: '16px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
  color: '#202124',
});

const StyledTableRow = styled(TableRow)({
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    transform: 'scale(1.01)',
  },
  '&:last-child td': {
    borderBottom: 'none',
  },
});

const selectStyles = {
  control: (base, state) => ({
    ...base,
    border: `1px solid ${state.isFocused ? "#667eea" : "rgba(0, 0, 0, 0.12)"} `,
    boxShadow: "none",
    background: "white",
    borderRadius: "12px",
    minHeight: "56px",
    height: "56px",
    fontFamily: "'DM Sans', sans-serif",
  }),
  placeholder: (defaultStyles) => {
    return {
      ...defaultStyles,
      color: "#5f6368",
      fontFamily: "'DM Sans', sans-serif",
    };
  },
  singleValue: (defaultStyles) => {
    return {
      ...defaultStyles,
      color: "#202124",
      fontFamily: "'DM Sans', sans-serif",
    };
  },
  option: (defaultStyles, state) => {
    return {
      ...defaultStyles,
      backgroundColor: state.isSelected ? "#667eea" : state.isFocused ? "#e3f2fd" : "white",
      color: state.isSelected ? "white" : "#202124",
      fontFamily: "'DM Sans', sans-serif",
      cursor: "pointer",
    };
  },
  menu: (defaultStyles) => {
    return {
      ...defaultStyles,
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      fontFamily: "'DM Sans', sans-serif",
    };
  },
};

export default function VideoCatalog(props) {
  const history = useHistory();

  const [catelogs, setCatelogs] = useState([]);
  const [loader, setLoader] = useState(true);
  const [count, setCount] = useState(0);

  const [filters, setFilters] = useState({
    sort: releaseFiltersForVideo[0],
    limit: 25,
    offset: 0,
    page: 1,
    search: "",
  });

  const getStatusDetails = (status) => {
    switch (status) {
      case 'approved':
        return { label: 'Approved', icon: <CheckCircleIcon />, color: '#4caf50' };
      case 'in_review':
        return { label: 'In Review', icon: <AccessTimeIcon />, color: '#ff9800' };
      case 'draft':
        return { label: 'Draft', icon: <BookmarksIcon />, color: '#2196f3' };
      case 'rejected':
        return { label: 'Rejected', icon: <CancelIcon />, color: '#f44336' };
      case 'takendown':
        return { label: 'Taken Down', icon: <RemoveCircleOutlineIcon />, color: '#757575' };
      default:
        return { label: status, icon: null, color: '#9e9e9e' };
    }
  };

  const delaySearch = useCallback(
    _.debounce((filters) => onGetCatelogList(filters), 500),
    []
  );

  useEffect(() => {
    onGetCatelogList(filters);
    setTimeout(() => {
      setLoader(false);
    }, 5000);
  }, []);

  const onGetCatelogList = (filters) => {
    props.setLoader(true);
    actions
      .getVReleaseHistory({ ...filters, sort: filters.sort.value })
      .then((res) => {
        props.setLoader(false);
        setCatelogs(res.body);
        setCount(res.total);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err.message);
      });
  };

  const onPageChange = (event, page) => {
    const offset = (page - 1) * filters.limit;
    const newFilters = { ...filters, page, offset };
    setFilters(newFilters);
    onGetCatelogList(newFilters);
  };

  const onChangeSearch = (e) => {
    const newFilters = { ...filters, search: e.target.value };
    setFilters(newFilters);
    delaySearch(newFilters);
  };

  const onSelectType = (e) => {
    const newFilters = { ...filters, offset: 0, page: 1, sort: e };
    setFilters(newFilters);
    onGetCatelogList(newFilters);
  };

  let totalPages = Math.ceil(Number(count) / filters.limit);

  return (
    <PageContainer>
      <Fade in timeout={800}>
        <Box>
          {/* Header Section */}
          <PageHeader>
            <Box>
              <Typography variant="h1">
                <VideoLibraryIcon sx={{ fontSize: 40, color: '#667eea' }} />
                Video Catalog
              </Typography>
              <Typography sx={{ fontSize: '1rem', color: '#5f6368', fontFamily: "'DM Sans', sans-serif" }}>
                Manage and track all your video releases in one place
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: '200px' }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '16px',
                padding: '16px 28px',
                boxShadow: '0 4px 16px rgba(102, 126, 234, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.35)',
                },
              }}>
                <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Total Releases
                </Typography>
                <Typography sx={{ fontSize: '2rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>
                  {count}
                </Typography>
              </Box>
              <AudioButton onClick={() => history.push("/catalog")}>
                Go To Audio Catalog
              </AudioButton>
            </Box>
          </PageHeader>

          {/* Search and Filter Section */}
          <Box sx={{ mb: 4 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <SearchField
                  type="text"
                  variant="outlined"
                  size="medium"
                  onChange={onChangeSearch}
                  value={filters.search}
                  placeholder="Search by title, actor, actress..."
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                        <SearchIcon sx={{ color: '#5f6368' }} />
                      </Box>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectCmp
                  styles={selectStyles}
                  placeholder="Filter by status..."
                  value={filters.sort}
                  onChange={onSelectType}
                  options={releaseFiltersForVideo}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Table Section */}
          <StyledTableContainer component={Paper}>
            <Table>
              <StyledTableHead>
                <TableRow>
                  <TableCell align="center">No.</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Artwork</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Audio</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>Actress</TableCell>
                  <TableCell>Release Date</TableCell>
                </TableRow>
              </StyledTableHead>

              <TableBody>
                {catelogs && catelogs.length ? (
                  catelogs.map((item, key) => {
                    const statusDetails = getStatusDetails(item.status);

                    return (
                      <StyledTableRow key={key}>
                        <StyledTableCell align="center">
                          {(filters.page - 1) * filters.limit + (key + 1)}
                        </StyledTableCell>
                        <StyledTableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <Box sx={{ color: statusDetails.color, display: 'inline-flex' }}>
                              {statusDetails.icon}
                            </Box>
                            <Typography sx={{
                              color: statusDetails.color,
                              fontWeight: 600,
                              fontSize: '0.875rem',
                              fontFamily: "'DM Sans', sans-serif"
                            }}>
                              {statusDetails.label}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ padding: '8px' }}>
                          <LightTooltip
                            title={
                              <Box>
                                <img
                                  src={item.thumbnail}
                                  alt={item.release_title}
                                  style={{
                                    width: '280px',
                                    height: '280px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                  }}
                                />
                              </Box>
                            }
                            placement="right"
                          >
                            <Box sx={{
                              width: '60px',
                              height: '60px',
                              margin: 'auto',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.1)',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
                              }
                            }}>
                              <img
                                src={item.thumbnail}
                                alt={item.release_title}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            </Box>
                          </LightTooltip>
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontWeight: 600, color: '#202124' }}>
                          {item.release_title}
                        </StyledTableCell>
                        <StyledTableCell>{item.audio_title}</StyledTableCell>
                        <StyledTableCell>{item.actor}</StyledTableCell>
                        <StyledTableCell>{item.actress}</StyledTableCell>
                        <StyledTableCell>
                          {moment(Number(item.release_date)).format("DD/MM/YYYY")}
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <StyledTableCell
                      align="center"
                      colSpan="8"
                      sx={{
                        fontWeight: 600,
                        fontSize: '1.125rem',
                        color: '#5f6368',
                        padding: '64px 16px',
                      }}
                    >
                      <VideoLibraryIcon sx={{ fontSize: 60, color: '#e0e0e0', mb: 2, display: 'block', margin: '0 auto 16px' }} />
                      No Video Release Found.
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {catelogs && catelogs.length > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Pagination
                  count={totalPages}
                  page={filters.page || 0}
                  onChange={onPageChange}
                  showFirstButton
                  showLastButton
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 600,
                      backgroundColor: 'white',
                      border: '2px solid #e0e0e0',
                      color: '#202124',
                      fontSize: '1rem',
                      minWidth: '40px',
                      height: '40px',
                      margin: '0 4px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        borderColor: '#667eea',
                        transform: 'translateY(-2px)',
                      },
                    },
                    '& .Mui-selected': {
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important',
                      color: 'white !important',
                      border: 'none !important',
                      transform: 'scale(1.1)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%) !important',
                      },
                    },
                    '& .MuiPaginationItem-ellipsis': {
                      color: '#5f6368',
                      fontWeight: 700,
                    },
                  }}
                />
              </Box>
            )}
          </StyledTableContainer>
        </Box>
      </Fade>
    </PageContainer>
  );
}
